import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { formatApiDateTimeDisplay } from '../lib/date'

const LOADED_FROM_LABEL = {
  DB_ACTIVE_VERSION: 'DB 버전',
  CLASSPATH_CSV: '샘플',
  LIVE_API: 'API 보강',
  EMPTY: '없음',
}

const TAB_LABELS = {
  'hira-institution': '요양기관',
  'kcd-master': 'KCD 상병마스터',
  'hira-disease-stats': 'HIRA 진료비통계지표',
}

const SOURCE_LINKS = {
  hiraInstitution: [
    { label: '공공데이터포털 병원정보서비스', url: 'https://www.data.go.kr/data/15001698/openapi.do' },
    { label: 'HIRA 보건의료빅데이터 Open API', url: 'https://opendata.hira.or.kr/op/opc/selectOpnsApi.do' },
  ],
  kcdMaster: [
    { label: 'HIRA 상병마스터 공지', url: 'https://www.hira.or.kr/rc/drug/insuadtcrtr/bbsView.do?brdBltNo=53037&brdScnBltNo=4&pgmid=HIRAA030069000400' },
    { label: '통계분류포털 KCD', url: 'https://kssc.kostat.go.kr' },
  ],
  hiraDiseaseStats: [
    { label: 'HIRA 진료비통계지표', url: 'https://www.hira.or.kr/bbsDummy.do?pgmid=HIRAA020045030000' },
    { label: 'HIRA 의료통계', url: 'https://opendata.hira.or.kr/op/opc/olapHthInsRvStatInfoTab21.do?docNo=03-037' },
  ],
}

function unwrapApiData(res) {
  const body = res?.data
  if (body?.success === false) {
    throw new Error(body?.message || '요청 처리에 실패했습니다.')
  }
  return body?.data !== undefined ? body.data : body
}

function formatDateTime(value) {
  return value ? formatApiDateTimeDisplay(undefined, value) : '-'
}

function formatNumber(value) {
  if (value == null || value === '') return '-'
  return Number(value || 0).toLocaleString('ko-KR')
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function waitForImportJob(jobKey, onUpdate) {
  if (!jobKey) return null
  const deadline = Date.now() + 30 * 60 * 1000
  let delay = 1500

  while (Date.now() < deadline) {
    const res = await api.get(`/operator/import-jobs/${encodeURIComponent(jobKey)}`, { __silent: true })
    const job = unwrapApiData(res) || {}
    onUpdate?.(job)

    if (job.status === 'SUCCEEDED') {
      return job
    }
    if (job.status === 'FAILED') {
      throw new Error(job.errorMessage || job.message || 'IMPORT_FAILED')
    }

    await sleep(delay)
    delay = Math.min(delay + 500, 5000)
  }

  throw new Error('IMPORT_JOB_TIMEOUT')
}

function StatusCard({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-bold text-slate-900">{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-slate-500">{sub}</div> : null}
    </div>
  )
}

function SourceLinks({ items }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm font-semibold text-slate-900">자료 출처</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {(items || []).map((item) => (
          <a
            key={item.url}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            href={item.url}
            target="_blank"
            rel="noreferrer"
          >
            {item.label} 바로가기
          </a>
        ))}
      </div>
    </div>
  )
}

function Message({ children, tone = 'slate' }) {
  if (!children) return null
  const toneClass = tone === 'error'
    ? 'border-red-200 bg-red-50 text-red-700'
    : tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : 'border-slate-200 bg-slate-50 text-slate-700'
  return <div className={`rounded-lg border px-3 py-2 text-sm ${toneClass}`}>{children}</div>
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      className={`px-4 py-2 text-sm font-semibold ${
        active
          ? 'border-b-2 border-brand-navy text-brand-navy'
          : 'text-slate-500 hover:text-slate-700'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function HiraInstitutionTab() {
  const fileRef = useRef(null)
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState('slate')
  const [label, setLabel] = useState('')

  const loadStatus = useCallback(async () => {
    setLoading(true)
    setMsg('')
    try {
      const res = await api.get('/admin/hira-medical-institutions/status')
      setStatus(unwrapApiData(res) || null)
    } catch (e) {
      setStatus(null)
      setMsg(e?.message || '상태를 불러오지 못했습니다.')
      setMsgTone('error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  const uploadFile = async (file) => {
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    fd.append('async', 'true')
    if (label.trim()) fd.append('label', label.trim())
    setUploading(true)
    setMsg('')
    try {
      const res = await api.post('/admin/hira-medical-institutions/versions', fd)
      let d = unwrapApiData(res)
      if (d?.jobKey) {
        setMsg(`업로드 접수됨: ${d.jobKey} · 서버에서 병의원 정보를 반영 중입니다.`)
        const job = await waitForImportJob(d.jobKey, (next) => {
          if (next.status === 'RUNNING') setMsg('서버에서 병의원 정보를 반영 중입니다.')
          if (next.status === 'QUEUED') setMsg('병의원 정보 업로드가 접수되어 처리 대기 중입니다.')
        })
        d = job?.result || {}
      }
      setMsg(`업로드 완료: ${formatNumber(d?.entryCount)}건 반영`)
      setMsgTone('success')
      await loadStatus()
    } catch (e) {
      setMsg(e?.message || '업로드에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const reload = async () => {
    setLoading(true)
    setMsg('')
    try {
      await api.post('/admin/hira-medical-institutions/reload')
      await loadStatus()
      setMsg('메모리에 다시 불러왔습니다.')
      setMsgTone('success')
    } catch (e) {
      setMsg(e?.message || '다시 불러오기에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard label="DB 건수" value={formatNumber(status?.dbActiveCount)} />
        <StatusCard label="기관명 인덱스" value={formatNumber(status?.nameIndexCount)} />
        <StatusCard label="보강 건수" value={formatNumber(status?.overrideCount)} />
        <StatusCard
          label="적용 소스"
          value={LOADED_FROM_LABEL[status?.loadedFrom] || '-'}
          sub={formatDateTime(status?.loadedAt)}
        />
      </div>

      <SourceLinks items={SOURCE_LINKS.hiraInstitution} />

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
          <h2 className="text-base font-bold text-slate-900">HIRA 요양기관 업로드</h2>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">버전 라벨</label>
            <input
              type="text"
              className="w-full max-w-md rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="2026.3 병원정보서비스"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => uploadFile(e.target.files?.[0])}
            />
            <button
              type="button"
              className="rounded-lg border border-teal-600 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? '업로드 중' : 'CSV / XLSX 업로드'}
            </button>
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={loading}
              onClick={reload}
            >
              다시 불러오기
            </button>
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={loading}
              onClick={loadStatus}
            >
              상태 새로고침
            </button>
          </div>

          <Message tone={msgTone}>{msg}</Message>
        </div>
      </div>
    </div>
  )
}

function KcdMasterTab() {
  const fileRef = useRef(null)
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [seeding, setSeeding] = useState(false)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState('slate')
  const [sourceVersion, setSourceVersion] = useState('')
  const [sheetName, setSheetName] = useState('')
  const [replace, setReplace] = useState(true)

  const loadStatus = useCallback(async () => {
    setLoading(true)
    setMsg('')
    try {
      const res = await api.get('/operator/kcd-master/status')
      setStatus(unwrapApiData(res) || null)
    } catch (e) {
      setStatus(null)
      setMsg(e?.message || 'KCD 마스터 상태를 불러오지 못했습니다.')
      setMsgTone('error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  const uploadFile = async (file) => {
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    fd.append('replace', String(replace))
    fd.append('async', 'true')
    if (sourceVersion.trim()) fd.append('sourceVersion', sourceVersion.trim())
    if (sheetName.trim()) fd.append('sheetName', sheetName.trim())
    setUploading(true)
    setMsg('')
    try {
      const res = await api.post('/operator/kcd-master/import-xlsx', fd)
      let d = unwrapApiData(res)
      if (d?.jobKey) {
        setMsg(`업로드 접수됨: ${d.jobKey} · 서버에서 반영 중입니다.`)
        const job = await waitForImportJob(d.jobKey, (next) => {
          if (next.status === 'RUNNING') setMsg('서버에서 KCD 상병마스터를 반영 중입니다.')
          if (next.status === 'QUEUED') setMsg('업로드가 접수되어 처리 대기 중입니다.')
        })
        d = job?.result || {}
      }
      setMsg(`업로드 완료: 처리 ${formatNumber(d?.processed)}건, 신규 ${formatNumber(d?.imported)}건, 갱신 ${formatNumber(d?.updated)}건`)
      setMsgTone('success')
      await loadStatus()
    } catch (e) {
      setMsg(e?.message || 'KCD 상병마스터 업로드에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const seedFromClasspath = async () => {
    setSeeding(true)
    setMsg('')
    try {
      const params = {
        replace,
        sourceVersion: sourceVersion.trim() || 'KCD_MASTER.csv',
      }
      const res = await api.post('/operator/kcd-master/seed-from-classpath', null, { params })
      const d = unwrapApiData(res)
      setMsg(`내장 CSV 반영 완료: 처리 ${formatNumber(d?.processed)}건, 신규 ${formatNumber(d?.imported)}건, 갱신 ${formatNumber(d?.updated)}건`)
      setMsgTone('success')
      await loadStatus()
    } catch (e) {
      setMsg(e?.message || '내장 CSV 반영에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setSeeding(false)
    }
  }

  const sourceVersions = Array.isArray(status?.sourceVersions) ? status.sourceVersions : []

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard label="활성 코드" value={formatNumber(status?.activeCount)} />
        <StatusCard label="전체 코드" value={formatNumber(status?.totalCount)} />
        <StatusCard label="별칭" value={formatNumber(status?.aliasCount)} />
        <StatusCard label="상태 확인" value={formatDateTime(status?.checkedAt)} />
      </div>

      <SourceLinks items={SOURCE_LINKS.kcdMaster} />

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
          <h2 className="text-base font-bold text-slate-900">KCD 상병마스터 업로드</h2>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">소스 버전</label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={sourceVersion}
                onChange={(e) => setSourceVersion(e.target.value)}
                placeholder="예: 배포용 상병마스터_260329.xlsx"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">시트명</label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={sheetName}
                onChange={(e) => setSheetName(e.target.value)}
                placeholder="비워두면 자동 선택"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={replace}
                onChange={(e) => setReplace(e.target.checked)}
              />
              기존 KCD 마스터를 교체
            </label>
          </div>

          <div className="space-y-3 rounded-lg bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-800">반영 작업</div>
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => uploadFile(e.target.files?.[0])}
              />
              <button
                type="button"
                className="rounded-lg border border-teal-600 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? '업로드 중' : 'XLSX 업로드'}
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                disabled={seeding}
                onClick={seedFromClasspath}
              >
                {seeding ? '반영 중' : '내장 CSV 반영'}
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                disabled={loading}
                onClick={loadStatus}
              >
                상태 새로고침
              </button>
            </div>
            <Message tone={msgTone}>{msg}</Message>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b px-4 py-3 text-sm font-semibold text-slate-900">최근 소스 버전</div>
        <div className="overflow-x-auto">
          <table className="min-w-[620px] w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500">
              <tr>
                <th className="px-3 py-2">소스</th>
                <th className="px-3 py-2">버전</th>
                <th className="px-3 py-2 text-right">건수</th>
              </tr>
            </thead>
            <tbody>
              {sourceVersions.length === 0 ? (
                <tr>
                  <td className="px-3 py-6 text-center text-slate-500" colSpan={3}>등록된 소스 버전이 없습니다.</td>
                </tr>
              ) : null}
              {sourceVersions.map((row, idx) => (
                <tr key={`${row.sourceName}-${row.sourceVersion}-${idx}`} className="border-t">
                  <td className="px-3 py-2">{row.sourceName || '-'}</td>
                  <td className="px-3 py-2">{row.sourceVersion || '-'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatNumber(row.rowCount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function statusBadge(status) {
  if (status === 'ACTIVE') return 'bg-emerald-50 text-emerald-700 ring-emerald-200'
  if (status === 'ARCHIVED') return 'bg-slate-100 text-slate-600 ring-slate-200'
  return 'bg-amber-50 text-amber-700 ring-amber-200'
}

function HiraDiseaseStatsTab() {
  const fileRef = useRef(null)
  const [status, setStatus] = useState(null)
  const [releases, setReleases] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [activatingId, setActivatingId] = useState(null)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState('slate')
  const [form, setForm] = useState({
    releaseName: '',
    periodStart: '',
    periodEnd: '',
    publishedAt: '',
    sourceUrl: '',
    notes: '',
    replace: true,
    activate: true,
  })

  const loadAll = useCallback(async () => {
    setLoading(true)
    setMsg('')
    try {
      const [statusRes, releaseRes] = await Promise.all([
        api.get('/operator/hira-stats/status'),
        api.get('/operator/hira-stats/releases', {
          params: { datasetName: 'disease_code_rank', page: 1, size: 50 },
        }),
      ])
      setStatus(unwrapApiData(statusRes) || null)
      const releaseData = unwrapApiData(releaseRes) || {}
      setReleases(Array.isArray(releaseData.items) ? releaseData.items : [])
    } catch (e) {
      setStatus(null)
      setReleases([])
      setMsg(e?.message || 'HIRA 진료비통계지표 상태를 불러오지 못했습니다.')
      setMsgTone('error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const uploadFile = async (file) => {
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    fd.append('datasetName', 'disease_code_rank')
    fd.append('replace', String(form.replace))
    fd.append('activate', String(form.activate))
    fd.append('async', 'true')
    if (form.releaseName.trim()) fd.append('releaseName', form.releaseName.trim())
    if (form.periodStart) fd.append('periodStart', form.periodStart)
    if (form.periodEnd) fd.append('periodEnd', form.periodEnd)
    if (form.publishedAt) fd.append('publishedAt', form.publishedAt)
    if (form.sourceUrl.trim()) fd.append('sourceUrl', form.sourceUrl.trim())
    if (form.notes.trim()) fd.append('notes', form.notes.trim())
    setUploading(true)
    setMsg('')
    try {
      const res = await api.post('/operator/hira-stats/import-disease-xlsx', fd)
      let d = unwrapApiData(res)
      if (d?.jobKey) {
        setMsg(`업로드 접수됨: ${d.jobKey} · 서버에서 반영 중입니다.`)
        const job = await waitForImportJob(d.jobKey, (next) => {
          if (next.status === 'RUNNING') setMsg('서버에서 HIRA 진료비통계지표를 반영 중입니다.')
          if (next.status === 'QUEUED') setMsg('업로드가 접수되어 처리 대기 중입니다.')
        })
        d = job?.result || {}
      }
      setMsg(`업로드 완료: 반영 ${formatNumber(d?.imported)}건, KCD 연결 ${formatNumber(d?.matched)}건, 코드만 ${formatNumber(d?.unmatched)}건`)
      setMsgTone('success')
      await loadAll()
    } catch (e) {
      setMsg(e?.message || 'HIRA 진료비통계지표 업로드에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const activateRelease = async (releaseId) => {
    if (!releaseId) return
    setActivatingId(releaseId)
    setMsg('')
    try {
      await api.post(`/operator/hira-stats/releases/${releaseId}/activate`)
      setMsg('선택한 진료비통계지표 릴리스를 활성화했습니다.')
      setMsgTone('success')
      await loadAll()
    } catch (e) {
      setMsg(e?.message || '릴리스 활성화에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setActivatingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard label="릴리스" value={formatNumber(status?.releaseCount)} />
        <StatusCard label="활성 릴리스" value={formatNumber(status?.activeReleaseCount)} />
        <StatusCard label="통계 항목" value={formatNumber(status?.metricCount)} />
        <StatusCard label="코드만 매칭" value={formatNumber(status?.unmatchedMetricCount)} sub={formatDateTime(status?.checkedAt)} />
      </div>

      <SourceLinks items={SOURCE_LINKS.hiraDiseaseStats} />

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
          <h2 className="text-base font-bold text-slate-900">HIRA 진료비통계지표 업로드</h2>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">릴리스명</label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.releaseName}
                onChange={(e) => updateForm('releaseName', e.target.value)}
                placeholder="예: 2023년 상반기 진료비통계지표"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">기간 시작</label>
              <input
                type="date"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.periodStart}
                onChange={(e) => updateForm('periodStart', e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">기간 종료</label>
              <input
                type="date"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.periodEnd}
                onChange={(e) => updateForm('periodEnd', e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">공표일</label>
              <input
                type="date"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.publishedAt}
                onChange={(e) => updateForm('publishedAt', e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">출처 URL</label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.sourceUrl}
                onChange={(e) => updateForm('sourceUrl', e.target.value)}
                placeholder="선택"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">메모</label>
              <textarea
                className="min-h-[80px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.notes}
                onChange={(e) => updateForm('notes', e.target.value)}
                placeholder="자료 설명, 수집 경로 등"
              />
            </div>
          </div>

          <div className="space-y-3 rounded-lg bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-800">반영 작업</div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={form.replace}
                onChange={(e) => updateForm('replace', e.target.checked)}
              />
              같은 릴리스명이 있으면 교체
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={form.activate}
                onChange={(e) => updateForm('activate', e.target.checked)}
              />
              업로드 후 바로 활성화
            </label>
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => uploadFile(e.target.files?.[0])}
              />
              <button
                type="button"
                className="rounded-lg border border-teal-600 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? '업로드 중' : 'XLSX 업로드'}
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                disabled={loading}
                onClick={loadAll}
              >
                상태 새로고침
              </button>
            </div>
            <Message tone={msgTone}>{msg}</Message>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b px-4 py-3 text-sm font-semibold text-slate-900">진료비통계지표 릴리스</div>
        <div className="overflow-x-auto">
          <table className="min-w-[860px] w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500">
              <tr>
                <th className="px-3 py-2">릴리스</th>
                <th className="px-3 py-2">기간</th>
                <th className="px-3 py-2">공표일</th>
                <th className="px-3 py-2">상태</th>
                <th className="px-3 py-2">활성화</th>
                <th className="px-3 py-2 text-right">작업</th>
              </tr>
            </thead>
            <tbody>
              {releases.length === 0 ? (
                <tr>
                  <td className="px-3 py-6 text-center text-slate-500" colSpan={6}>등록된 진료비통계지표 릴리스가 없습니다.</td>
                </tr>
              ) : null}
              {releases.map((row) => (
                <tr key={row.id} className="border-t align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{row.releaseName || '-'}</div>
                    <div className="text-xs text-slate-500">{row.datasetName || '-'}</div>
                  </td>
                  <td className="px-3 py-2">{row.periodStart || '-'} ~ {row.periodEnd || '-'}</td>
                  <td className="px-3 py-2">{row.publishedAt || '-'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded px-2 py-1 text-xs font-semibold ring-1 ${statusBadge(row.status)}`}>
                      {row.status || '-'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{formatDateTime(row.activatedAt)}</td>
                  <td className="px-3 py-2 text-right">
                    {row.status === 'ACTIVE' ? (
                      <span className="text-xs font-semibold text-emerald-700">사용 중</span>
                    ) : (
                      <button
                        type="button"
                        className="rounded border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        disabled={activatingId === row.id}
                        onClick={() => activateRelease(row.id)}
                      >
                        {activatingId === row.id ? '활성화 중' : '활성화'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default function RawDataAdminPage() {
  const [tab, setTab] = useState('hira-institution')

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
            <h1 className="text-xl font-bold text-slate-900">RAW 데이터관리</h1>
          </div>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-1 border-b border-slate-200">
        {Object.entries(TAB_LABELS).map(([key, label]) => (
          <TabButton key={key} active={tab === key} onClick={() => setTab(key)}>
            {label}
          </TabButton>
        ))}
      </div>

      {tab === 'hira-institution' ? <HiraInstitutionTab /> : null}
      {tab === 'kcd-master' ? <KcdMasterTab /> : null}
      {tab === 'hira-disease-stats' ? <HiraDiseaseStatsTab /> : null}

      <div className="mt-8 text-xs text-slate-500">
        <Link className="text-brand-navy hover:underline" to="/data/design-support-reference">
          질병·특약·수술명 관리
        </Link>
        {' · '}
        <Link className="text-brand-navy hover:underline" to="/data/insurers">
          원수사 마스터
        </Link>
      </div>
    </div>
  )
}
